import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { HeartPulse, Check, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { localISODate, formatDate, LONG_DATE } from "@/lib/dates";
import { useI18n } from "@/context/I18nContext";
import { SectionHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const today = () => localISODate();
const TYPES = [
  { v: "run", id: "Lari", en: "Running" },
  { v: "cycle", id: "Sepeda", en: "Cycling" },
  { v: "swim", id: "Renang", en: "Swimming" },
  { v: "walk", id: "Jalan Cepat", en: "Walking" },
  { v: "hiit", id: "HIIT", en: "HIIT" },
  { v: "row", id: "Rowing", en: "Rowing" },
  { v: "other", id: "Lainnya", en: "Other" },
];

export default function Cardio() {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [type, setType] = useState("run");
  const [duration, setDuration] = useState("");
  const [distance, setDistance] = useState("");
  const [calories, setCalories] = useState("");
  const [saving, setSaving] = useState(false);
  const L = lang === "id" ? "id" : "en";

  const { data: logs } = useQuery({ queryKey: ["cardio"], queryFn: async () => (await api.get("/cardio")).data });
  const typeLabel = (v) => (TYPES.find((x) => x.v === v) || {})[L] || v;

  const save = async () => {
    if (!duration) return toast.error(t("cardioDuration"));
    setSaving(true);
    try {
      await api.post("/cardio", {
        type, duration_min: parseFloat(duration) || 0,
        distance_km: parseFloat(distance) || 0, calories: parseFloat(calories) || 0, date: today(),
      });
      toast.success(t("save"));
      setDuration(""); setDistance(""); setCalories("");
      qc.invalidateQueries({ queryKey: ["cardio"] });
    } catch { toast.error("Error"); } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <SectionHeader title={t("cardio")} subtitle={formatDate(new Date(), lang, LONG_DATE)} />

      <div className="rounded-2xl border bg-card p-5" data-testid="cardio-form">
        <div className="mb-4 flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/15 text-primary"><HeartPulse className="h-5 w-5" /></span>
          <h3 className="font-display font-semibold">{t("logCardio")}</h3>
        </div>
        <div className="space-y-4">
          <div>
            <Label>{t("cardioType")}</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1.5" data-testid="cardio-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((x) => <SelectItem key={x.v} value={x.v}>{x[L]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">{t("cardioDuration")}</Label><Input type="number" inputMode="numeric" value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-1.5 tabular-nums" data-testid="cardio-duration" /></div>
            <div><Label className="text-xs">{t("cardioDistance")}</Label><Input type="number" step="0.1" inputMode="decimal" value={distance} onChange={(e) => setDistance(e.target.value)} className="mt-1.5 tabular-nums" data-testid="cardio-distance" /></div>
            <div><Label className="text-xs">{t("caloriesBurned")}</Label><Input type="number" inputMode="numeric" value={calories} onChange={(e) => setCalories(e.target.value)} className="mt-1.5 tabular-nums" data-testid="cardio-calories" /></div>
          </div>
          <Button onClick={save} disabled={saving} className="w-full gap-2" data-testid="cardio-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t("save")}
          </Button>
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-display font-semibold">{t("recentCardio")}</h3>
        {logs?.length ? (
          <div className="space-y-2" data-testid="cardio-list">
            {logs.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-xl border bg-card px-4 py-3" data-testid={`cardio-${c.id}`}>
                <div>
                  <div className="font-display font-semibold">{typeLabel(c.type)}</div>
                  <div className="text-xs text-muted-foreground tabular-nums">{formatDate(c.date, lang)} · {c.duration_min} min{c.distance_km ? ` · ${c.distance_km} km` : ""}</div>
                </div>
                {c.calories ? <span className="rounded-full bg-warning/15 px-2.5 py-1 text-xs font-semibold text-warning tabular-nums">{c.calories} kcal</span> : null}
              </div>
            ))}
          </div>
        ) : <EmptyState icon={HeartPulse} title={t("cardio")} description={t("noCardio")} />}
      </div>
    </div>
  );
}
